import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, resolveDirective as _resolveDirective, withDirectives as _withDirectives, normalizeClass as _normalizeClass, renderList as _renderList, unref as _unref } from "vue"

const _hoisted_1 = { class: "organization-info" }
const _hoisted_2 = { class: "title-row" }
const _hoisted_3 = { class: "tabs-panel" }
const _hoisted_4 = { class: "panel-actions" }
const _hoisted_5 = { class: "panel-actions" }
const _hoisted_6 = { key: 0 }
const _hoisted_7 = { key: 1 }
const _hoisted_8 = {
  key: 2,
  class: "apply-authorization-dialog-container"
}
const _hoisted_9 = { class: "drawer-footer" }
const _hoisted_10 = { class: "drawer-footer" }

import { fetchOrganizationInfoApi } from "@/api/organization/organizationApi";
import { getName } from "@/util/i18nUtil";
import {
  fetchOrganizationShipListApi,
  createShipApi,
  submitShipAuthorizationApplyApi,
  getShipAuthorityListApi,
} from "@/api/productApi";
import { fetchUserListApi, createUserApi } from "@/api/user/userApi";
import { Organization } from "@/types/organization";
import {
  ApplyShipAuthorizationParams,
  Ship,
  ShipAuthorization,
  ShipVO,
} from "@/types/product";
import { User } from "@/types/user";
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import type { FormInstance, FormRules } from "element-plus";
import { ElMessage } from "element-plus";
import { useI18n } from "vue-i18n";
import { Authority } from "@/types/resource";
import { formatToIsoOffset } from "@/util/datetimeUtil";


export default /*@__PURE__*/_defineComponent({
  __name: 'OrganizationInfoView',
  setup(__props) {

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const orgId = computed(() => route.query.orgId as string);

const organization = ref<Organization>();
const isEditingName = ref(false);
const editName = ref("");
const savingName = ref(false);

const activeTab = ref("users");

const fetchOrganizationInfo = async () => {
  const response = await fetchOrganizationInfoApi(orgId.value);
  if (response) {
    organization.value = response;
  }
};

const userList = ref<User[]>([]);
const loadingUsers = ref(false);
const fetchUserList = async () => {
  loadingUsers.value = true;
  try {
    const response = await fetchUserListApi(orgId.value);
    if (response) {
      userList.value = response;
    }
  } finally {
    loadingUsers.value = false;
  }
};

const shipList = ref<ShipVO[]>([]);
const loadingShips = ref(false);
const fetchShipList = async () => {
  loadingShips.value = true;
  try {
    const response = await fetchOrganizationShipListApi(orgId.value);
    if (response) {
      shipList.value = response;
    }
  } finally {
    loadingShips.value = false;
  }
};

// 编辑名称交互
const startEditName = () => {
  if (!organization.value) return;
  editName.value = organization.value.name;
  isEditingName.value = true;
};

const cancelEditName = () => {
  isEditingName.value = false;
};

const saveName = async () => {
  if (!organization.value) return;
  savingName.value = true;
  try {
    await editOrgInfo();
    organization.value.name = editName.value;
    isEditingName.value = false;
  } finally {
    savingName.value = false;
  }
};

// 详情跳转
const goUserInfo = (row: User) => {
  router.push({ name: "userInfo", query: { userId: row.id } });
};

const goShipInfo = (row: Ship) => {
  router.push({ name: "shipInfo", query: { shipId: row.id } });
};

const editOrgInfo = async () => {
  // TODO
};

const createShip = async () => {
  if (!shipFormRef.value) return;
  await shipFormRef.value.validate(async (valid) => {
    if (!valid) return;
    shipDrawer.value.loading = true;
    try {
      const payload = {
        orgId: orgId.value,
        name: shipForm.value.name,
        ccsCertificationId: shipForm.value.ccsCertificationId || undefined,
        projectId: shipForm.value.projectId || undefined,
        deliveryDate: shipForm.value.deliveryDate || undefined,
        description: shipForm.value.description || undefined,
      } as unknown as Ship;

      const ok = await createShipApi(payload);
      if (ok) {
        ElMessage.success("新增成功");
        shipDrawer.value.visible = false;
        resetShipForm();
        fetchShipList();
      } else {
        ElMessage.error("新增失败");
      }
    } catch (e) {
      ElMessage.error("新增失败");
    } finally {
      shipDrawer.value.loading = false;
    }
  });
};

// 开通集团账号抽屉与表单（创建的是用户端集团账号，非管理端登录账号）
const userDrawer = ref({ visible: false, loading: false });
const userFormRef = ref<FormInstance>();
const userForm = ref({
  orgId: orgId.value,
  name: "",
  username: "",
  password: "",
  re_password: "",
  role: null as unknown as number | null,
  phone: "",
  email: "",
  department: "",
  address: "",
  person: "",
  description: "",
});

const usernamePattern = /^[A-Za-z0-9._-]{8,20}$/; // 字母数字及 . _ -
const passwordPattern = /^[A-Za-z0-9!@#$%^&*()_+\-=.]{8,20}$/; // 常见符号，禁止空格

const validateConfirm = (
  rule: any,
  value: string,
  callback: (e?: Error) => void
) => {
  if (!value) return callback(new Error("请再次输入密码"));
  if (value !== userForm.value.password)
    return callback(new Error("两次输入密码不一致"));
  callback();
};

const userRules: FormRules = {
  name: [
    { required: true, message: "请输入姓名", trigger: "blur" },
    { min: 1, max: 255, message: "长度不超过255字节", trigger: "blur" },
  ],
  username: [
    { required: true, message: "请输入用户名", trigger: "blur" },
    {
      pattern: usernamePattern,
      message: "8-20位，字母数字和._-，不含空格",
      trigger: ["blur", "change"],
    },
  ],
  // 角色规则已删除：开户固定为集团管理员（role=1），表单不再提供角色选择
  password: [
    { required: true, message: "请输入密码", trigger: "blur" },
    {
      pattern: passwordPattern,
      message: "8-20位，允许字母数字及常见符号，不含空格",
      trigger: ["blur", "change"],
    },
  ],
  re_password: [
    { required: true, message: "请再次输入密码", trigger: "blur" },
    { validator: validateConfirm, trigger: ["blur", "change"] },
  ],
  phone: [{ max: 255, message: "长度不超过255字节", trigger: "blur" }],
  email: [
    { type: "email", message: "邮箱格式不正确", trigger: ["blur", "change"] },
    { max: 255, message: "长度不超过255字节", trigger: "blur" },
  ],
  department: [{ max: 255, message: "长度不超过255字节", trigger: "blur" }],
  address: [{ max: 255, message: "长度不超过255字节", trigger: "blur" }],
  person: [{ max: 255, message: "长度不超过255字节", trigger: "blur" }],
  description: [{ max: 255, message: "长度不超过255字节", trigger: "blur" }],
};

const resetUserForm = () => {
  userForm.value = {
    orgId: orgId.value,
    name: "",
    username: "",
    password: "",
    re_password: "",
    phone: "",
    email: "",
    department: "",
    address: "",
    person: "",
    role: null,
    description: "",
  };
  userFormRef.value?.clearValidate();
};

// 新增船舶抽屉与表单
const shipDrawer = ref({ visible: false, loading: false });
const shipFormRef = ref<FormInstance>();
const shipForm = ref({
  orgId: orgId.value,
  name: "",
  ccsCertificationId: "",
  projectId: "",
  deliveryDate: "",
  description: "",
});

const getUtf8ByteLength = (str: string): number => {
  try {
    return new TextEncoder().encode(str).length;
  } catch (e) {
    // 兜底（极端环境）
    return unescape(encodeURIComponent(str)).length;
  }
};

const validateShipName = (
  rule: any,
  value: string,
  callback: (e?: Error) => void
) => {
  if (!value) return callback(new Error("请输入名称"));
  if (/\s/.test(value)) return callback(new Error("名称不能包含空格"));
  if (getUtf8ByteLength(value) > 255)
    return callback(new Error("长度不超过255字节"));
  callback();
};

const validateMax255 = (
  rule: any,
  value: string,
  callback: (e?: Error) => void
) => {
  if (value && getUtf8ByteLength(value) > 255)
    return callback(new Error("长度不超过255字节"));
  callback();
};

const shipRules: FormRules = {
  name: [
    { required: true, message: "请输入名称", trigger: "blur" },
    { validator: validateShipName, trigger: ["blur", "change"] },
  ],
  ccsCertificationId: [{ validator: validateMax255, trigger: "blur" }],
  projectId: [{ validator: validateMax255, trigger: "blur" }],
  deliveryDate: [],
  description: [{ validator: validateMax255, trigger: "blur" }],
};

const resetShipForm = () => {
  shipForm.value = {
    orgId: orgId.value,
    name: "",
    ccsCertificationId: "",
    projectId: "",
    deliveryDate: "",
    description: "",
  };
  shipFormRef.value?.clearValidate();
};

const openCreateShipDrawer = () => {
  resetShipForm();
  shipDrawer.value.visible = true;
};

const onShipDrawerBeforeClose = (done: () => void) => {
  if (shipDrawer.value.loading) return; // 提交中禁止关闭
  done();
};

const onShipDrawerCancel = () => {
  if (shipDrawer.value.loading) return;
  shipDrawer.value.visible = false;
};

const openCreateUserDrawer = () => {
  resetUserForm();
  userDrawer.value.visible = true;
};

const onUserDrawerBeforeClose = (done: () => void) => {
  if (userDrawer.value.loading) return; // 提交中禁止关闭
  done();
};

const onUserDrawerCancel = () => {
  if (userDrawer.value.loading) return;
  userDrawer.value.visible = false;
};

const createUser = async () => {
  if (!userFormRef.value) return;
  await userFormRef.value.validate(async (valid) => {
    if (!valid) return;
    userDrawer.value.loading = true;
    try {
      const payload = {
        orgId: orgId.value,
        name: userForm.value.name,
        username: userForm.value.username,
        password: userForm.value.password,
        // 开户固定为集团管理员（后端同样强制 role=1，双保险）
        role: 1,
        phone: userForm.value.phone || undefined,
        email: userForm.value.email || undefined,
        department: userForm.value.department || undefined,
        address: userForm.value.address || undefined,
        person: userForm.value.person || undefined,
        description: userForm.value.description || undefined,
        company: "",
        country: "",
      } as unknown as User;

      const ok = await createUserApi(payload);
      if (ok) {
        ElMessage.success("添加成功");
        userDrawer.value.visible = false;
        resetUserForm();
        fetchUserList();
      } else {
        ElMessage.error("添加失败");
      }
    } catch (e) {
      ElMessage.error("添加失败");
    } finally {
      userDrawer.value.loading = false;
    }
  });
};

// 船只授权
const shipAuthorityList = ref<Authority[]>([]);
const fetchShipAuthorityList = async () => {
  const response = await getShipAuthorityListApi();
  if (response) shipAuthorityList.value = response;
};

const selectedShip = ref<Ship>();
const applyShipAuthorizationParams = reactive<ApplyShipAuthorizationParams>({
  shipId: "",
  startTime: "",
  endTime: "",
  authorityId: null,
  description: "",
  orgId: orgId.value,
});
const applyAuthorization = async (ship: ShipVO) => {
  console.log(ship.id);
  selectedShip.value = ship.ship;
  applyShipAuthorizationParams.shipId = ship.id;
  applyAuthorizationDialogVisible.value = true;
};

// 提交授权请求
const applyAuthorizationDialogVisible = ref<boolean>(false);
const datetimeRange = ref<[Date, Date] | []>([]);
watch(datetimeRange, (value) => {
  if (Array.isArray(value) && value.length === 2) {
    applyShipAuthorizationParams.startTime = formatToIsoOffset(value[0]);
    applyShipAuthorizationParams.endTime = formatToIsoOffset(value[1]);
  } else {
    applyShipAuthorizationParams.startTime = "";
    applyShipAuthorizationParams.endTime = "";
  }
});
const shortcuts = [
  {
    text: t("UIV.threeHours"),
    value: () => {
      const start = new Date();
      const end = new Date();
      end.setHours(end.getHours() + 3);
      return [start, end];
    },
  },
  {
    text: t("UIV.oneDay"),
    value: () => {
      const start = new Date();
      const end = new Date();
      end.setDate(end.getDate() + 1);
      return [start, end];
    },
  },
  {
    text: t("UIV.oneWeek"),
    value: () => {
      const start = new Date();
      const end = new Date();
      end.setDate(end.getDate() + 7);
      return [start, end];
    },
  },
  {
    text: t("UIV.oneMonth"),
    value: () => {
      const start = new Date();
      const end = new Date();
      end.setMonth(end.getMonth() + 1);
      return [start, end];
    },
  },
];
const btnSubmitShipAuthorizationApplyLoading = ref<boolean>(false);
const applyShipAuthorizationSubmitResult = ref<number>();
const submitShipAuthorizationApply = async () => {
  const params = applyShipAuthorizationParams;
  if (params.shipId == "") {
    applyShipAuthorizationSubmitResult.value = 0;
    return;
  }
  if (params.startTime == "") {
    ElMessage.error(t("UIV.warning.selectStartTime"));
    return;
  }
  if (params.endTime == "") {
    ElMessage.error(t("UIV.warning.selectEndTime"));
    return;
  }
  if (params.authorityId == null) {
    ElMessage.error(t("UIV.warning.selectAuthority"));
    return;
  }
  btnSubmitShipAuthorizationApplyLoading.value = true;
  const response = await submitShipAuthorizationApplyApi(
    applyShipAuthorizationParams
  );
  btnSubmitShipAuthorizationApplyLoading.value = false;
  console.log(response);
  if (response === "ok") {
    applyShipAuthorizationSubmitResult.value = 1;
    fetchShipList();
  } else {
    applyShipAuthorizationSubmitResult.value = 0;
  }
};

// 关闭Dialog窗口
const closeShipAuorizationDialog = () => {
  handleApplyAuthorizationDialogClose(() => {
    applyAuthorizationDialogVisible.value = false;
  });
};

// 处理Dialog关闭事件
const handleApplyAuthorizationDialogClose = (done: () => void) => {
  done();
  setTimeout(() => {
    selectedShip.value = undefined;
    datetimeRange.value = [];
    Object.assign(applyShipAuthorizationParams, {
      authorityId: null,
      endTime: "",
      shipId: "",
      startTime: "",
      description: "",
      orgId: orgId.value,
    });
    btnSubmitShipAuthorizationApplyLoading.value = false;
    applyShipAuthorizationSubmitResult.value = undefined;
  }, 300);
};

const getAuthorizationStatus = (
  authorization: ShipAuthorization | undefined
) => {
  if (!authorization) return t("UIV.unauthorized");
  if (authorization.status == 0) return t("UIV.unauthorized");
  else if (authorization.status == 1) return t("UIV.authorized");
  else return t("UIV.authorizing");
};
const getAuthorizationStatusClass = (
  authorization: ShipAuthorization | undefined
) => {
  if (!authorization) return "text-red";
  if (authorization.status == 0) return "text-red";
  else if (authorization.status == 1) return "text-green";
  else return "text-yellow";
};

const goOrganizationList = () => {
  router.push({ name: "organizationList" });
};

onMounted(() => {
  fetchOrganizationInfo();
  fetchUserList();
  fetchShipList();
  fetchShipAuthorityList();
});

return (_ctx: any,_cache: any) => {
  const _component_ArrowLeft = _resolveComponent("ArrowLeft")!
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_tooltip = _resolveComponent("el-tooltip")!
  const _component_el_descriptions_item = _resolveComponent("el-descriptions-item")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_descriptions = _resolveComponent("el-descriptions")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_table = _resolveComponent("el-table")!
  const _component_el_tab_pane = _resolveComponent("el-tab-pane")!
  const _component_el_result = _resolveComponent("el-result")!
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_date_picker = _resolveComponent("el-date-picker")!
  const _component_el_dialog = _resolveComponent("el-dialog")!
  const _component_el_tabs = _resolveComponent("el-tabs")!
  const _component_el_alert = _resolveComponent("el-alert")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_form = _resolveComponent("el-form")!
  const _component_el_drawer = _resolveComponent("el-drawer")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_el_descriptions, {
      column: 2,
      border: "",
      size: "small"
    }, {
      title: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_el_tooltip, {
            content: "返回集团列表",
            placement: "bottom"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_el_button, {
                class: "back-btn",
                type: "primary",
                plain: "",
                circle: "",
                onClick: goOrganizationList
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_icon, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_ArrowLeft)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _cache[23] || (_cache[23] = _createElementVNode("div", { class: "desc-title" }, "集团信息", -1))
        ])
      ]),
      default: _withCtx(() => [
        _createVNode(_component_el_descriptions_item, { label: "ID" }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(organization.value?.id || "-"), 1)
          ]),
          _: 1
        }),
        _createVNode(_component_el_descriptions_item, { label: "名称" }, {
          default: _withCtx(() => [
            (!isEditingName.value)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createElementVNode("span", null, _toDisplayString(organization.value?.name || "-"), 1),
                  (organization.value)
                    ? (_openBlock(), _createBlock(_component_el_button, {
                        key: 0,
                        type: "primary",
                        link: "",
                        style: {"margin-left":"8px"},
                        onClick: startEditName
                      }, {
                        default: _withCtx(() => [...(_cache[24] || (_cache[24] = [
                          _createTextVNode("编辑", -1)
                        ]))]),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ], 64))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _createVNode(_component_el_input, {
                    modelValue: editName.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((editName).value = $event)),
                    size: "small",
                    placeholder: "请输入集团名称",
                    style: {"max-width":"240px","margin-right":"8px"}
                  }, null, 8, ["modelValue"]),
                  _createVNode(_component_el_button, {
                    type: "primary",
                    size: "small",
                    loading: savingName.value,
                    onClick: saveName
                  }, {
                    default: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                      _createTextVNode("保存", -1)
                    ]))]),
                    _: 1
                  }, 8, ["loading"]),
                  _createVNode(_component_el_button, {
                    size: "small",
                    onClick: cancelEditName
                  }, {
                    default: _withCtx(() => [...(_cache[26] || (_cache[26] = [
                      _createTextVNode("取消", -1)
                    ]))]),
                    _: 1
                  })
                ], 64))
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(_component_el_tabs, {
        modelValue: activeTab.value,
        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((activeTab).value = $event))
      }, {
        default: _withCtx(() => [
          _createVNode(_component_el_tab_pane, {
            label: "客户列表",
            name: "users"
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_4, [
                _createVNode(_component_el_button, {
                  type: "primary",
                  onClick: openCreateUserDrawer
                }, {
                  default: _withCtx(() => [...(_cache[27] || (_cache[27] = [
                    _createTextVNode("开通集团管理员账号", -1)
                  ]))]),
                  _: 1
                })
              ]),
              _withDirectives((_openBlock(), _createBlock(_component_el_table, {
                class: "data-table",
                data: userList.value,
                border: "",
                height: "100%",
                size: "small",
                stripe: "",
                "highlight-current-row": ""
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_table_column, {
                    fixed: "",
                    type: "index",
                    label: "ID",
                    "min-width": "40"
                  }),
                  _createVNode(_component_el_table_column, {
                    fixed: "",
                    prop: "name",
                    label: "姓名",
                    "min-width": "120"
                  }),
                  _createVNode(_component_el_table_column, {
                    prop: "username",
                    label: "用户名",
                    "min-width": "140"
                  }),
                  _createVNode(_component_el_table_column, {
                    prop: "phone",
                    label: "电话",
                    "min-width": "120"
                  }),
                  _createVNode(_component_el_table_column, {
                    prop: "email",
                    label: "邮箱",
                    "min-width": "180"
                  }),
                  _createVNode(_component_el_table_column, {
                    prop: "company",
                    label: "公司",
                    "min-width": "140"
                  }),
                  _createVNode(_component_el_table_column, {
                    prop: "department",
                    label: "部门",
                    "min-width": "120"
                  }),
                  _createVNode(_component_el_table_column, {
                    prop: "address",
                    label: "地址",
                    "min-width": "180"
                  }),
                  _createVNode(_component_el_table_column, {
                    prop: "country",
                    label: "国家",
                    "min-width": "100"
                  }),
                  _createVNode(_component_el_table_column, {
                    prop: "person",
                    label: "联系人",
                    "min-width": "120"
                  }),
                  _createVNode(_component_el_table_column, {
                    prop: "description",
                    label: "描述",
                    "min-width": "200",
                    "show-overflow-tooltip": ""
                  }),
                  _createVNode(_component_el_table_column, {
                    prop: "createtime",
                    label: "创建时间",
                    "min-width": "160"
                  }),
                  _createVNode(_component_el_table_column, {
                    prop: "updatetime",
                    label: "更新时间",
                    "min-width": "160"
                  }),
                  _createVNode(_component_el_table_column, {
                    fixed: "right",
                    label: "操作",
                    width: "100"
                  }, {
                    default: _withCtx(({ row }) => [
                      _createVNode(_component_el_button, {
                        type: "primary",
                        link: "",
                        onClick: ($event: any) => (goUserInfo(row))
                      }, {
                        default: _withCtx(() => [...(_cache[28] || (_cache[28] = [
                          _createTextVNode("查看详情", -1)
                        ]))]),
                        _: 1
                      }, 8, ["onClick"])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["data"])), [
                [_directive_loading, loadingUsers.value]
              ])
            ]),
            _: 1
          }),
          _createVNode(_component_el_tab_pane, {
            label: "船舶列表",
            name: "ships"
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(_component_el_button, {
                  type: "primary",
                  onClick: openCreateShipDrawer
                }, {
                  default: _withCtx(() => [...(_cache[29] || (_cache[29] = [
                    _createTextVNode("新增船舶", -1)
                  ]))]),
                  _: 1
                })
              ]),
              _withDirectives((_openBlock(), _createBlock(_component_el_table, {
                class: "data-table",
                data: shipList.value,
                border: "",
                height: "100%",
                size: "small",
                stripe: "",
                "highlight-current-row": ""
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_table_column, {
                    fixed: "",
                    type: "index",
                    label: "ID",
                    "min-width": "40"
                  }),
                  _createVNode(_component_el_table_column, {
                    fixed: "",
                    prop: "ship.name",
                    label: "名称",
                    "min-width": "140"
                  }),
                  _createVNode(_component_el_table_column, {
                    prop: "status.cname",
                    label: "在线状态",
                    "min-width": "100"
                  }),
                  _createVNode(_component_el_table_column, {
                    prop: "ship.ccsCertificationId",
                    label: "CCS证书ID",
                    "min-width": "160"
                  }),
                  _createVNode(_component_el_table_column, {
                    prop: "ship.projectId",
                    label: "项目ID",
                    "min-width": "140"
                  }),
                  _createVNode(_component_el_table_column, {
                    prop: "ship.deliveryDate",
                    label: "交付日期",
                    "min-width": "140"
                  }),
                  _createVNode(_component_el_table_column, {
                    prop: "ship.createtime",
                    label: "创建时间",
                    "min-width": "160"
                  }),
                  _createVNode(_component_el_table_column, {
                    prop: "ship.updatetime",
                    label: "更新时间",
                    "min-width": "160"
                  }),
                  _createVNode(_component_el_table_column, {
                    prop: "ship.description",
                    label: "描述",
                    "min-width": "200",
                    "show-overflow-tooltip": ""
                  }),
                  _createVNode(_component_el_table_column, {
                    fixed: "right",
                    label: "授权时间",
                    width: "160"
                  }, {
                    default: _withCtx(({ row }) => [
                      (row.authorization)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(_ctx.$d(new Date(row.authorization.startTime), "long")), 1))
                        : _createCommentVNode("", true),
                      (row.authorization)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(_ctx.$d(new Date(row.authorization.endTime), "long")), 1))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_el_table_column, {
                    fixed: "right",
                    label: "授权状态",
                    width: "80"
                  }, {
                    default: _withCtx(({ row }) => [
                      _createElementVNode("p", {
                        class: _normalizeClass(["authorization-status-text", getAuthorizationStatusClass(row.authorization)])
                      }, _toDisplayString(getAuthorizationStatus(row.authorization)), 3)
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_el_table_column, {
                    fixed: "right",
                    label: "操作",
                    width: "100"
                  }, {
                    default: _withCtx(({ row }) => [
                      (row.authorization)
                        ? (_openBlock(), _createBlock(_component_el_button, {
                            key: 0,
                            type: "primary",
                            disabled: row.authorization.status != 1,
                            link: "",
                            onClick: ($event: any) => (goShipInfo(row))
                          }, {
                            default: _withCtx(() => [...(_cache[30] || (_cache[30] = [
                              _createTextVNode(" 查看详情 ", -1)
                            ]))]),
                            _: 1
                          }, 8, ["disabled", "onClick"]))
                        : (_openBlock(), _createBlock(_component_el_button, {
                            key: 1,
                            type: "success",
                            link: "",
                            onClick: ($event: any) => (applyAuthorization(row))
                          }, {
                            default: _withCtx(() => [...(_cache[31] || (_cache[31] = [
                              _createTextVNode(" 获取授权 ", -1)
                            ]))]),
                            _: 1
                          }, 8, ["onClick"]))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["data"])), [
                [_directive_loading, loadingShips.value]
              ]),
              _createVNode(_component_el_dialog, {
                modelValue: applyAuthorizationDialogVisible.value,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((applyAuthorizationDialogVisible).value = $event)),
                title: _ctx.$t('UIV.applyAuthorization'),
                "before-close": handleApplyAuthorizationDialogClose,
                width: "500"
              }, {
                default: _withCtx(() => [
                  (applyShipAuthorizationSubmitResult.value == 1)
                    ? (_openBlock(), _createBlock(_component_el_result, {
                        key: 0,
                        icon: "success",
                        title: _ctx.$t('UIV.success.title'),
                        "sub-title": _ctx.$t('UIV.success.tip')
                      }, {
                        extra: _withCtx(() => [
                          _createVNode(_component_el_button, {
                            type: "primary",
                            onClick: closeShipAuorizationDialog
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_ctx.$t("UIV.close")), 1)
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }, 8, ["title", "sub-title"]))
                    : (applyShipAuthorizationSubmitResult.value == 0)
                      ? (_openBlock(), _createBlock(_component_el_result, {
                          key: 1,
                          icon: "error",
                          title: _ctx.$t('UIV.failed.title'),
                          "sub-title": _ctx.$t('UIV.failed.tip')
                        }, {
                          extra: _withCtx(() => [
                            _createVNode(_component_el_button, {
                              type: "primary",
                              onClick: closeShipAuorizationDialog
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(_ctx.$t("UIV.close")), 1)
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }, 8, ["title", "sub-title"]))
                      : (_openBlock(), _createElementBlock("div", _hoisted_8, [
                          _createElementVNode("h1", null, _toDisplayString(selectedShip.value?.name), 1),
                          _createVNode(_component_el_select, {
                            modelValue: applyShipAuthorizationParams.authorityId,
                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((applyShipAuthorizationParams.authorityId) = $event)),
                            placeholder: _ctx.$t('UIV.selectShipAuthority')
                          }, {
                            default: _withCtx(() => [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(shipAuthorityList.value, (item) => {
                                return (_openBlock(), _createBlock(_component_el_option, {
                                  key: item.id,
                                  label: _unref(getName)(item),
                                  value: item.id
                                }, null, 8, ["label", "value"]))
                              }), 128))
                            ]),
                            _: 1
                          }, 8, ["modelValue", "placeholder"]),
                          _createVNode(_component_el_date_picker, {
                            modelValue: datetimeRange.value,
                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((datetimeRange).value = $event)),
                            style: {"width":"100%"},
                            type: "datetimerange",
                            shortcuts: shortcuts,
                            "range-separator": "To",
                            "start-placeholder": "Start date",
                            "end-placeholder": "End date"
                          }, null, 8, ["modelValue"]),
                          _createVNode(_component_el_input, {
                            modelValue: applyShipAuthorizationParams.description,
                            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((applyShipAuthorizationParams.description) = $event)),
                            placeholder: _ctx.$t('UIV.descriptionPlaceHolder'),
                            type: "textarea",
                            rows: "5",
                            resize: "none",
                            style: {"margin-top":"20px"}
                          }, null, 8, ["modelValue", "placeholder"]),
                          _createVNode(_component_el_button, {
                            style: {"width":"100%","margin-top":"20px"},
                            type: "primary",
                            loading: btnSubmitShipAuthorizationApplyLoading.value,
                            onClick: submitShipAuthorizationApply
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_ctx.$t("UIV.submitShipAuthorizationApply")), 1)
                            ]),
                            _: 1
                          }, 8, ["loading"])
                        ]))
                ]),
                _: 1
              }, 8, ["modelValue", "title"])
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    _createVNode(_component_el_drawer, {
      modelValue: userDrawer.value.visible,
      "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((userDrawer.value.visible) = $event)),
      title: "开通集团管理员账号",
      size: "420px",
      "with-header": true,
      "close-on-click-modal": !userDrawer.value.loading,
      "close-on-press-escape": !userDrawer.value.loading,
      "before-close": onUserDrawerBeforeClose,
      direction: "rtl",
      "append-to-body": ""
    }, {
      default: _withCtx(() => [
        _createVNode(_component_el_alert, {
          type: "info",
          closable: false,
          "show-icon": "",
          style: {"margin-bottom":"16px"}
        }, {
          title: _withCtx(() => [...(_cache[32] || (_cache[32] = [
            _createTextVNode(" 此处创建的是", -1),
            _createElementVNode("b", null, "用户端集团管理员账号", -1),
            _createTextVNode("（交付给客户集团登录用户端使用）， 并非管理端登录账号。账号创建后交由客户，本集团其他账号（区域管理员 / 普通账号）由客户在用户端自行创建。 ", -1)
          ]))]),
          _: 1
        }),
        _createVNode(_component_el_form, {
          ref_key: "userFormRef",
          ref: userFormRef,
          model: userForm.value,
          rules: userRules,
          "label-width": "96px",
          "status-icon": ""
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_form_item, {
              label: "姓名",
              prop: "name"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: userForm.value.name,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((userForm.value.name) = $event)),
                  disabled: userDrawer.value.loading,
                  placeholder: "请输入姓名"
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "用户名",
              prop: "username"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: userForm.value.username,
                  "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((userForm.value.username) = $event)),
                  disabled: userDrawer.value.loading,
                  placeholder: "8-20位，字母数字和._-"
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "密码",
              prop: "password"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: userForm.value.password,
                  "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((userForm.value.password) = $event)),
                  disabled: userDrawer.value.loading,
                  "show-password": "",
                  placeholder: "8-20位，允许常见符号"
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "确认密码",
              prop: "re_password"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: userForm.value.re_password,
                  "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((userForm.value.re_password) = $event)),
                  disabled: userDrawer.value.loading,
                  "show-password": "",
                  placeholder: "再次输入密码"
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "电话",
              prop: "phone"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: userForm.value.phone,
                  "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((userForm.value.phone) = $event)),
                  disabled: userDrawer.value.loading,
                  placeholder: "选填"
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "邮箱",
              prop: "email"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: userForm.value.email,
                  "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((userForm.value.email) = $event)),
                  disabled: userDrawer.value.loading,
                  placeholder: "选填，邮箱格式"
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "部门",
              prop: "department"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: userForm.value.department,
                  "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((userForm.value.department) = $event)),
                  disabled: userDrawer.value.loading,
                  placeholder: "选填"
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "地址",
              prop: "address"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: userForm.value.address,
                  "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((userForm.value.address) = $event)),
                  disabled: userDrawer.value.loading,
                  placeholder: "选填"
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "联系人",
              prop: "person"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: userForm.value.person,
                  "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((userForm.value.person) = $event)),
                  disabled: userDrawer.value.loading,
                  placeholder: "选填"
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "描述",
              prop: "description"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: userForm.value.description,
                  "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((userForm.value.description) = $event)),
                  disabled: userDrawer.value.loading,
                  type: "textarea",
                  rows: 3,
                  placeholder: "选填"
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createElementVNode("div", _hoisted_9, [
              _createVNode(_component_el_button, {
                type: "success",
                loading: userDrawer.value.loading,
                onClick: createUser
              }, {
                default: _withCtx(() => [...(_cache[33] || (_cache[33] = [
                  _createTextVNode("提交", -1)
                ]))]),
                _: 1
              }, 8, ["loading"]),
              _createVNode(_component_el_button, {
                type: "danger",
                disabled: userDrawer.value.loading,
                onClick: onUserDrawerCancel
              }, {
                default: _withCtx(() => [...(_cache[34] || (_cache[34] = [
                  _createTextVNode("取消", -1)
                ]))]),
                _: 1
              }, 8, ["disabled"])
            ])
          ]),
          _: 1
        }, 8, ["model"])
      ]),
      _: 1
    }, 8, ["modelValue", "close-on-click-modal", "close-on-press-escape"]),
    _createVNode(_component_el_drawer, {
      modelValue: shipDrawer.value.visible,
      "onUpdate:modelValue": _cache[22] || (_cache[22] = ($event: any) => ((shipDrawer.value.visible) = $event)),
      title: "新增船舶",
      size: "420px",
      "with-header": true,
      "close-on-click-modal": !shipDrawer.value.loading,
      "close-on-press-escape": !shipDrawer.value.loading,
      "before-close": onShipDrawerBeforeClose,
      direction: "rtl",
      "append-to-body": ""
    }, {
      default: _withCtx(() => [
        _createVNode(_component_el_form, {
          ref_key: "shipFormRef",
          ref: shipFormRef,
          model: shipForm.value,
          rules: shipRules,
          "label-width": "120px",
          "status-icon": ""
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_form_item, {
              label: "名称",
              prop: "name"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: shipForm.value.name,
                  "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((shipForm.value.name) = $event)),
                  disabled: shipDrawer.value.loading,
                  placeholder: "请输入名称（必填）"
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "CCS证书ID",
              prop: "ccsCertificationId"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: shipForm.value.ccsCertificationId,
                  "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((shipForm.value.ccsCertificationId) = $event)),
                  disabled: shipDrawer.value.loading,
                  placeholder: "选填"
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "项目ID",
              prop: "projectId"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: shipForm.value.projectId,
                  "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((shipForm.value.projectId) = $event)),
                  disabled: shipDrawer.value.loading,
                  placeholder: "选填"
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "交付日期",
              prop: "deliveryDate"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_date_picker, {
                  modelValue: shipForm.value.deliveryDate,
                  "onUpdate:modelValue": _cache[20] || (_cache[20] = ($event: any) => ((shipForm.value.deliveryDate) = $event)),
                  disabled: shipDrawer.value.loading,
                  type: "date",
                  placeholder: "选填",
                  "value-format": "YYYY-MM-DD",
                  style: {"width":"100%"}
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "描述",
              prop: "description"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: shipForm.value.description,
                  "onUpdate:modelValue": _cache[21] || (_cache[21] = ($event: any) => ((shipForm.value.description) = $event)),
                  disabled: shipDrawer.value.loading,
                  type: "textarea",
                  rows: 3,
                  placeholder: "选填"
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createElementVNode("div", _hoisted_10, [
              _createVNode(_component_el_button, {
                type: "success",
                loading: shipDrawer.value.loading,
                onClick: createShip
              }, {
                default: _withCtx(() => [...(_cache[35] || (_cache[35] = [
                  _createTextVNode("提交", -1)
                ]))]),
                _: 1
              }, 8, ["loading"]),
              _createVNode(_component_el_button, {
                type: "danger",
                disabled: shipDrawer.value.loading,
                onClick: onShipDrawerCancel
              }, {
                default: _withCtx(() => [...(_cache[36] || (_cache[36] = [
                  _createTextVNode("取消", -1)
                ]))]),
                _: 1
              }, 8, ["disabled"])
            ])
          ]),
          _: 1
        }, 8, ["model"])
      ]),
      _: 1
    }, 8, ["modelValue", "close-on-click-modal", "close-on-press-escape"])
  ]))
}
}

})